// What the Plan room keeps outside the documents: the Plan editor over the live project (`planEdit`: each of
// `PlanEditor`'s typed writes is one `LiveProject.edit`, so it's batched and synced like any edit), the per-browser
// choices its header controls and pages share (the corkboard's layout, the lanes, the heatmap, the outliner's grouping —
// the Mac's @AppStorage), and the per-device opened-at map cold scenes read (UserDefaults `openedAt-<project>` on the
// Mac; localStorage here, recorded when Write opens fragments: `recordOpened`).
//
// The pure parts (collections as a flat arrangement, heat by node, sorting and grouping rows) are in planModel.ts, so
// the unit tests run them in Node.
import { storageId } from './storageId.ts';
import { useSyncExternalStore } from 'react';
import { parseTags, TAGS_FIELD, type DocumentEditor, type ProjectEditor } from '@writing-suite/core';
import { newRule, PlanEditor, type SavedSearchRule } from '@writing-suite/core/plan';
import type { LiveProject } from './project.ts';

/** One of `PlanEditor`'s writes, through the live project (its changes shown and batched as `edit`'s are). */
export function planEdit<T>(live: Pick<LiveProject, 'edit'>, fn: (e: PlanEditor) => T): T {
  return live.edit((editor: ProjectEditor) => {
    const d = editor as unknown as DocumentEditor;
    return fn(new PlanEditor(d.docs, d.context));
  });
}

// MARK: Shared per-browser choices

const PREFIX = 'ws:';
const values = new Map<string, unknown>();
const listeners = new Map<string, Set<() => void>>();

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** A choice as this browser keeps it (the value given when it has none, or it can't be read). */
export function readPreference<T>(key: string, initial: T): T {
  if (values.has(key)) return values.get(key) as T;
  let value = initial;
  try {
    const raw = storage()?.getItem(PREFIX + key);
    if (raw !== null && raw !== undefined) value = JSON.parse(raw) as T;
  } catch {
    // the default
  }
  values.set(key, value);
  return value;
}

export function writePreference<T>(key: string, value: T) {
  values.set(key, value);
  try {
    storage()?.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // kept for this page only
  }
  for (const fn of listeners.get(key) ?? []) fn();
}

/** `usePreference`, but every component using the key sees each change (a header menu and its page). */
export function useSharedPreference<T>(key: string, initial: T): [T, (v: T) => void] {
  const value = useSyncExternalStore(
    (fn) => {
      const set = listeners.get(key) ?? new Set();
      set.add(fn);
      listeners.set(key, set);
      return () => set.delete(fn);
    },
    () => readPreference(key, initial),
    () => initial,
  );
  return [value, (v: T) => writePreference(key, v)];
}

// MARK: The corkboard's and outliner's choices (the Mac's AppStorage keys)

/** `board`: a journal's posts in a column per Status (views/plan/PostBoard.tsx); journals only. */
export type CorkboardLayout = 'grid' | 'freeform' | 'swimlanes' | 'board';
export const LAYOUTS: { id: CorkboardLayout; name: string }[] = [
  { id: 'grid', name: 'Grid' },
  { id: 'freeform', name: 'Freeform' },
  { id: 'swimlanes', name: 'Swimlanes' },
  { id: 'board', name: 'Board' },
];

export const PREF = {
  layout: 'corkboardLayout',
  /** A journal's posts have their own layout in this browser, the Board until another is chosen. */
  journalLayout: 'journalCorkboardLayout',
  laneField: 'swimlaneField',
  lanesAligned: 'swimlaneAligned',
  emptyLanes: 'swimlaneEmpty',
  heat: 'showRevisionHeat',
  heatDays: 'heatmapDays',
  coldDays: 'coldDays',
  outlinerGroup: 'outlinerGroup',
  /** With the contents filtered: keep every card in place and fade those the filter leaves out (else they're hidden). */
  dimsFilteredOut: 'planDimsFilteredOut',
  outlinerSort: 'outlinerSort',
  outlinerHidden: (project: string) => `outline-hidden:${project}`,
  /** The outliner view in use (the Mac's UserDefaults `outlinerView-<project>`). */
  outlinerView: (project: string) => `outlinerView-${project}`,
  /** The contents' filter in use (the Mac's UserDefaults `binderFilter-<project>`; store/binderFilter.ts). */
  binderFilter: (project: string) => `binderFilter-${project}`,
  /** The shown collection's rules folded away in the filter bar (the Mac's `collectionSummaryFolded`). */
  collectionSummaryFolded: 'collectionSummaryFolded',
  /** With the contents filtered: draw what the filter leaves out faded rather than hide it (localStorage `ws:binderDims`,
   *  the Mac's `binderDimsFilteredOut`). */
  binderDims: 'binderDims',
} as const;

/** "Mark Scenes Cold After": days, 0 = Never. */
export const COLD_CHOICES = [30, 60, 90, 180, 365, 0];

// MARK: Opened on this device (cold scenes)

const openedKey = (project: string) => `openedAt-${storageId(project)}`;

/** When each fragment was last opened in Write in this browser. */
export function openedAt(project: string): Record<string, Date> {
  const out: Record<string, Date> = {};
  try {
    const raw = storage()?.getItem(openedKey(project));
    const stored = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    for (const [id, iso] of Object.entries(stored)) {
      const d = new Date(iso);
      if (Number.isFinite(d.getTime())) out[id] = d;
    }
  } catch {
    // nothing opened here yet
  }
  return out;
}

/** Write opened these fragments now (the Mac records it when the editor opens them). */
export function recordOpened(project: string, fragmentIds: readonly string[], now: Date = new Date()) {
  if (!fragmentIds.length) return;
  const s = storage();
  if (!s) return;
  try {
    const raw = s.getItem(openedKey(project));
    const stored = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    const iso = now.toISOString();
    for (const id of fragmentIds) stored[id] = iso;
    s.setItem(openedKey(project), JSON.stringify(stored));
  } catch {
    // not remembered: the scenes may show as cold
  }
}

// MARK: Save as Collection (from Find in Project)

/** A collection to start from: Find in Project's words, in its scope. */
export interface CollectionRequest {
  text: string;
  scope: 'manuscript' | 'all-text';
  /** Rules other than Text (the filter's, when it's saved from the bar). */
  rules?: SavedSearchRule[];
}

let request: CollectionRequest | null = null;
const requestListeners = new Set<() => void>();

/** Asks the contents' collection menu to open the collection editor with a Text rule for these words. */
export function requestCollection(r: CollectionRequest) {
  request = r;
  for (const fn of requestListeners) fn();
}

/** The request waiting (the collection menu takes it with `takeCollectionRequest`). */
export function useCollectionRequest(): CollectionRequest | null {
  return useSyncExternalStore(
    (fn) => {
      requestListeners.add(fn);
      return () => requestListeners.delete(fn);
    },
    () => request,
    () => null,
  );
}

export function takeCollectionRequest() {
  request = null;
  for (const fn of requestListeners) fn();
}

// MARK: One-click filtering (the Mac's `filterBy`)

/** Facets to filter the contents by: alone (`adding` false), or added to the filter in use. */
export interface FilterRequest {
  rules: SavedSearchRule[];
  adding: boolean;
}

const filterListeners = new Set<(r: FilterRequest) => void>();

/** Asks the open project's contents to filter by these facets (a tag chip, a value anywhere in the app). */
export function requestFilter(rules: SavedSearchRule[], adding = false) {
  for (const fn of filterListeners) fn({ rules, adding });
}

/** The shell's side: each request as it's made. */
export function onFilterRequest(fn: (r: FilterRequest) => void): () => void {
  filterListeners.add(fn);
  return () => filterListeners.delete(fn);
}

/** `LibraryModel.showTag`: filters the contents by one tag (everything tagged with it, in its place in the book). */
export function showTag(tag: string) {
  const name = parseTags(tag)[0];
  if (name) requestFilter([newRule({ kind: 'field', field: TAGS_FIELD, value: name })]);
}
